import React, { forwardRef, useCallback, useRef, useImperativeHandle, } from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import EditableColumn from './customCellRenderers/EditableColumn';
import { getStopPrefixAndField } from './columns';
const gridOpts = {
    defaultColDef: {
        enableRowGroup: true,
        enablePivot: false,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        cellRenderer: EditableColumn,
    },
    rowDragManaged: true,
    copyHeadersToClipboard: true,
    suppressColumnVirtualisation: true,
};
const ShipmentExplorer = forwardRef(function ShipmentExplorer({ items, getRowId, components, statusBar, columnDefs, onSortChanged, onSelectionChanged, onRowDisplayed, stopGroupStats, onFilterChange, onEditingStopped, shouldFetchData, fetchData, localeText, loadingText, sidebarParams }, ref) {
    const gridRef = useRef(null);
    const apiRef = useRef(null);
    const columnApiRef = useRef(null);
    useImperativeHandle(ref, () => ({
        get api() {
            return apiRef.current;
        },
        get columnApi() {
            return columnApiRef.current;
        },
        getSelectedRows: () => {
            if (!apiRef.current) {
                return [];
            }
            return apiRef.current.getSelectedRows().map((s) => (Object.assign(Object.assign({}, s), { _id: s.shipment_oid, id: s.shipment_oid })));
        },
    }));
    const handleColumnVisible = useCallback((event) => {
        var _a;
        const { visible, column } = event;
        const colId = column === null || column === void 0 ? void 0 : column.colId;
        if (!colId) {
            return;
        }
        const res = getStopPrefixAndField(colId);
        if (!res.isStopGroupField) {
            return;
        }
        const count = stopGroupStats[res.prefix];
        if (count === 1) {
            return;
        }
        const { prefix, field } = res;
        const otherColumns = Array.from(Array(count).keys())
            .filter((index) => {
            var _a;
            const stopGroupField = `${prefix}__${index}__${field}`;
            if (colId === stopGroupField) {
                return false;
            }
            const col = (_a = columnApiRef.current) === null || _a === void 0 ? void 0 : _a.getColumn(stopGroupField);
            if (!col || col.visible === visible) {
                return false;
            }
            return true;
        })
            .map((index) => {
            const colId = `${prefix}__${index}__${field}`;
            return {
                colId,
                hide: !visible,
            };
        });
        if (otherColumns.length === 0) {
            return;
        }
        (_a = apiRef.current) === null || _a === void 0 ? void 0 : _a.applyColumnState({
            state: otherColumns,
            applyOrder: true,
        });
    }, [stopGroupStats]);
    const onGridReady = useCallback((params) => {
        const { api, columnApi } = params;
        // Store api references
        apiRef.current = api;
        columnApiRef.current = columnApi;
        // Initialize grid
        api.showLoadingOverlay();
        api.autoSizeColumns();
        // Set up event listeners
        api.addEventListener('modelUpdated', () => {
            if (onRowDisplayed) {
                onRowDisplayed();
            }
        });
        api.addEventListener('columnVisible', handleColumnVisible);
        if (onFilterChange) {
            api.addEventListener('filterChanged', onFilterChange);
        }
        if (onEditingStopped) {
            api.addEventListener('cellEditingStopped', onEditingStopped);
        }
        // Fetch data if needed
        if (shouldFetchData && fetchData) {
            fetchData();
        }
    }, [
        onRowDisplayed,
        handleColumnVisible,
        onFilterChange,
        onEditingStopped,
        shouldFetchData,
        fetchData,
    ]);
    return (React.createElement("div", { "data-testid": "div-da5f52c8", className: "h-full w-full" },
        React.createElement(AgGridReact, { ref: gridRef, rowData: items, maintainColumnOrder: true, rememberGroupStateWhenNewData: true, animateRows: true, getRowId: getRowId, suppressClickEdit: true, suppressRowClickSelection: true, enableCellTextSelection: true, enableRangeSelection: true, rowSelection: "multiple", components: components, multiSortKey: "ctrl", onGridReady: onGridReady, onSortChanged: onSortChanged, statusBar: statusBar, columnDefs: columnDefs, defaultColDef: gridOpts.defaultColDef, onSelectionChanged: onSelectionChanged, localeText: localeText, overlayLoadingTemplate: `<div><span class="text-base text-green font-bold">${loadingText}</span><img src="https://3qjglz4aka5y3jyx13q7je0p-wpengine.netdna-ssl.com/wp-content/uploads/2021/01/logo-header.svg" alt="" class="img-fluid"></div>`, sideBar: sidebarParams, allowDragFromColumnsToolPanel: true })));
});
ShipmentExplorer.propTypes = {
    items: PropTypes.array,
    getRowId: PropTypes.func.isRequired,
    components: PropTypes.object,
    statusBar: PropTypes.object,
    columnDefs: PropTypes.array,
    onSortChanged: PropTypes.func.isRequired,
    onSelectionChanged: PropTypes.func.isRequired,
    onRowDisplayed: PropTypes.func,
    stopGroupStats: PropTypes.object,
    onFilterChange: PropTypes.func,
    onEditingStopped: PropTypes.func,
    shouldFetchData: PropTypes.bool,
    fetchData: PropTypes.func,
    localeText: PropTypes.object,
    loadingText: PropTypes.string,
};
ShipmentExplorer.defaultProps = {
    items: [],
    components: {},
    statusBar: {},
    columnDefs: [],
    onRowDisplayed: undefined,
    stopGroupStats: {},
    onFilterChange: undefined,
    onEditingStopped: undefined,
    shouldFetchData: false,
    fetchData: undefined,
    localeText: {},
    loadingText: 'Loading...',
};
export default ShipmentExplorer;
